import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';

import { backendApis } from '@/backendApis';
import { hivemindGroupsQueryOptions } from '@/features/hivemind/hivemind.query';
import { queryClient } from '@/query/client';

export const sameText = (a: string, b: string) =>
  a.toLowerCase() === b.toLowerCase();

const invalidateGroups = () =>
  queryClient.invalidateQueries({
    queryKey: hivemindGroupsQueryOptions().queryKey,
  });

export const useGroupsQuery = ({
  enabled,
  staleTime,
}: {
  enabled: boolean;
  staleTime?: number;
}) =>
  useQuery(
    { ...hivemindGroupsQueryOptions(), enabled, staleTime },
    queryClient
  );

/** Saving replaces a group's members. A new name for an existing group saves the new one and then deletes the old. */
export const useSaveGroupMutation = () =>
  useMutation(
    {
      mutationFn: async ({
        name,
        memberEmails,
        previousName,
      }: {
        name: string;
        memberEmails: string[];
        previousName: string | null;
      }) => {
        await backendApis.hive.saveGroup(name, memberEmails);
        if (previousName && !sameText(previousName, name)) {
          await backendApis.hive.deleteGroup(previousName);
        }
      },
      onSuccess: invalidateGroups,
      onError: () => toast.error("Couldn't save the group. Try again."),
    },
    queryClient
  );

export const useDeleteGroupMutation = () =>
  useMutation(
    {
      mutationFn: (name: string) => backendApis.hive.deleteGroup(name),
      onSuccess: invalidateGroups,
      onError: () => toast.error("Couldn't delete the group. Try again."),
    },
    queryClient
  );
